import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { io } from 'socket.io-client';
import agentsApi from '../services/agents.api';
import { API_BASE_URL, API_CONFIG } from '../config/api.config';
import { useAuth } from '../auth/AuthProviderUnified';
import logger from '../utils/logger';

/**
 * AIAgentContext - Context for managing AI agent proposal counts
 * 
 * Provides:
 * - pendingProposalsCount: Number of pending AI agent proposals
 * - loading: Loading state for initial fetch
 * - error: Error state if fetch fails
 * 
 * Features:
 * - Polls every 30 seconds for updates
 * - Uses Socket.IO for real-time updates
 * - Role-based access control (only GC, SC, Admin roles)
 * - Memoized context value to prevent unnecessary re-renders
 */

const AIAgentContext = createContext(null);

/**
 * Roles that have access to AI Intelligence features
 */
const AI_INTELLIGENCE_ROLES = new Set([
  'gcadmin',
  'scadmin',
  'admin',
  'industrial',
  'industrialadmin',
]);

/**
 * Check if user role has AI Intelligence access
 */
const hasAIAccess = (userRole) => {
  if (!userRole) return false;
  const normalizedRole = userRole.toLowerCase();
  return AI_INTELLIGENCE_ROLES.has(normalizedRole);
};

/**
 * AIAgentProvider - Provides AI agent proposal count context
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} [props.forceEnabled=false] - Force enable even without role access (for testing)
 */
export function AIAgentProvider({ children, forceEnabled = false }) {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(null); // null = loading, number = loaded
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Check if user has AI Intelligence access
  const hasAccess = forceEnabled || hasAIAccess(user?.role);

  // Fetch pending proposals count
  const fetchPendingCount = useCallback(async () => {
    if (!hasAccess) {
      setPendingCount(0);
      return;
    }

    try {
      const proposals = await agentsApi.getAgentProposals({ status: 'pending' });
      const count = proposals?.length || 0;
      setPendingCount(count);
      setError(null);
      logger.debug('[AIAgentContext] Fetched pending proposals count', { count });
    } catch (err) {
      logger.error('[AIAgentContext] Failed to fetch pending proposals count', err);
      setError(err);
      // Fallback to 0 on error to avoid showing stale data
      setPendingCount(0);
    }
  }, [hasAccess]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasAccess) {
      setPendingCount(0);
      return;
    }

    fetchPendingCount();
  }, [hasAccess, fetchPendingCount]);

  // Poll every 30 seconds and set up Socket.IO for real-time updates
  useEffect(() => {
    if (!hasAccess) {
      return;
    }

    // Clear any existing poll interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Set up polling every 30 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchPendingCount();
    }, 30000); // 30 seconds

    if (!API_CONFIG.enableRealTimeUpdates) {
      logger.debug('[AIAgentContext] Socket.IO disabled; proposal count uses polling only');
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }

    // Set up Socket.IO for real-time updates
    const backendUrl = API_BASE_URL;
    const token = localStorage.getItem('vh_auth_token') || localStorage.getItem('accessToken');

    logger.info('[AIAgentContext] Connecting to Socket.IO for proposal updates...', { backendUrl });

    const socket = io(backendUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: false,
      timeout: 5000,
    });

    socketRef.current = socket;

    socket.on('connected', (data) => {
      logger.info('[AIAgentContext] Socket.IO connected', data);
    });

    socket.on('disconnect', (reason) => {
      logger.warn('[AIAgentContext] Socket.IO disconnected', { reason });
    });

    socket.on('connect_error', (error) => {
      logger.warn('[AIAgentContext] Socket.IO unavailable; proposal count uses polling only', {
        message: error?.message,
      });
      socket.disconnect();
      socketRef.current = null;
    });

    // Listen for proposal updates
    socket.on('proposal.updated', (event) => {
      logger.info('[AIAgentContext] Proposal updated event received', event);
      // Refetch count when proposals are approved/rejected
      fetchPendingCount();
    });

    socket.on('proposal.created', (event) => {
      logger.info('[AIAgentContext] Proposal created event received', event);
      // Refetch count when new proposals are created
      fetchPendingCount();
    });

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (socketRef.current) {
        logger.info('[AIAgentContext] Disconnecting Socket.IO');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [hasAccess, fetchPendingCount]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      pendingProposalsCount: pendingCount === null ? undefined : pendingCount,
      loading: pendingCount === null && hasAccess,
      error,
      hasAccess,
    }),
    [pendingCount, error, hasAccess]
  );

  return <AIAgentContext.Provider value={value}>{children}</AIAgentContext.Provider>;
}

AIAgentProvider.propTypes = {
  children: PropTypes.node.isRequired,
  forceEnabled: PropTypes.bool,
};

/**
 * useAIAgentContext - Hook to access AI agent context
 * 
 * @returns {Object} Context value with pendingProposalsCount, loading, error, hasAccess
 * @throws {Error} If used outside AIAgentProvider
 */
export function useAIAgentContext() {
  const context = useContext(AIAgentContext);
  if (!context) {
    // Return default values instead of throwing to allow graceful degradation
    logger.warn('[AIAgentContext] useAIAgentContext used outside AIAgentProvider, returning defaults');
    return {
      pendingProposalsCount: undefined,
      loading: false,
      error: null,
      hasAccess: false,
    };
  }
  return context;
}

export default AIAgentContext;
